/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  InputNumber,
  Modal,
  DatePicker,
} from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, RedoOutlined } from '@ant-design/icons';
import getFormattedValues, { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import { DATE_FRONTEND_FORMAT } from 'utils/normalizers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import { getPuestos } from 'api/catalogos/puestos';
import { getPlazasvistaOptimized } from 'api/catalogos/plazas';
import {
  getFirmadosDeDocumentos,
  getFirmadoDeDocumento,
  postFirmadoDeDocumento,
  patchFirmadoDeDocumento,
  deleteFirmadoDeDocumento,
  getDatosDePlazaFirmado,
  getModulosDeDocumentos,
  getDocumentosOperativos,
  getTiposDeFirma,
  getTiposDeImpresion,
  getRolesDeFirma,
} from 'api/configuracion/firmadoDeDocumentos';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
} from 'utils/constants';

const modelPermissionName = 'firmadodedocumento';

export const configuradorFirmadoDeDocumentosPermission = {
  permissionModel: modelPermissionName,
  catalogsModels: [
    'estadosglobales',
    'modulodedocumento',
    'documentooperativo',
    'tipodefirma',
    'tipodeimpresion',
    'catalogodefinalidaddefirmado',
    'puesto',
    'plaza',
  ],
  tabsModels: [],
};

// Campos que solo se muestran en el formulario (se resuelven desde la Plaza); no se envían.
const CAMPOS_SOLO_LECTURA = ['directorio_de_area_descripcion', 'empleado_descripcion'];

const ConfiguradorFirmadoDeDocumentos = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedData, setSelectedData] = useState({});
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [visibleCancelar, setVisibleCancelar] = useState(false);
  const [visibleReactivar, setVisibleReactivar] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [tiposDeFirma, setTiposDeFirma] = useState([]);
  const [tiposDeImpresion, setTiposDeImpresion] = useState([]);
  const [rolesDeFirma, setRolesDeFirma] = useState([]);
  const [puestos, setPuestos] = useState([]);
  const [plazas, setPlazas] = useState([]);
  const [disableFields, setDisableFields] = useState(false);
  // Un registro Autorizado sin Vigencia Final solo admite capturarla.
  const [soloVigenciaFinal, setSoloVigenciaFinal] = useState(false);

  const formInstances = [form];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getFirmadosDeDocumentos();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _modulos,
        _tiposDeFirma,
        _tiposDeImpresion,
        _rolesDeFirma,
        _puestos,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getModulosDeDocumentos(),
        getTiposDeFirma(),
        getTiposDeImpresion(),
        getRolesDeFirma(),
        getPuestos(),
        getFirmadosDeDocumentos(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setModulos(_modulos);
        setTiposDeFirma(_tiposDeFirma);
        setTiposDeImpresion(_tiposDeImpresion);
        setRolesDeFirma(_rolesDeFirma);
        setPuestos(_puestos);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line
  }, []);

  const cargarDocumentos = async (modulo) => {
    if (!modulo) {
      setDocumentos([]);
      return;
    }
    setDocumentos(await getDocumentosOperativos({ modulo }));
  };

  const cargarPlazas = async (puesto) => {
    if (!puesto) {
      setPlazas([]);
      return;
    }
    // /catalogos/plaza/ siempre pagina (10 por página); la vista optimizada regresa la
    // lista completa ya filtrada por la entidad del usuario.
    const _plazas = await getPlazasvistaOptimized({ puesto_id: puesto });
    setPlazas(Array.isArray(_plazas) ? _plazas : []);
  };

  const cargarDatosDePlaza = async (plaza) => {
    if (!plaza) {
      form.setFieldsValue({
        directorio_de_area_descripcion: undefined,
        empleado_descripcion: undefined,
      });
      return;
    }
    const datos = await getDatosDePlazaFirmado(plaza);
    form.setFieldsValue({
      directorio_de_area_descripcion: datos?.directorio_de_area_descripcion,
      empleado_descripcion: datos?.empleado_descripcion,
    });
  };

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setVisibleCancelar(false);
    setVisibleReactivar(false);
    setDisableFields(false);
    setSoloVigenciaFinal(false);
    setSelectedRowKeys([]);
    setSelectedData({});
    setDocumentos([]);
    setPlazas([]);
  };

  const onClickEdit = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const _firmado = await getFirmadoDeDocumento(key);
    if (_firmado) {
      await Promise.all([
        cargarDocumentos(_firmado.modulo),
        cargarPlazas(_firmado.puesto),
      ]);
      form.resetFields();
      form.setFieldsValue({
        ...formatReceived(_firmado),
        directorio_de_area_descripcion: _firmado.directorio_de_area_actual_descripcion,
        empleado_descripcion: _firmado.empleado_actual_descripcion,
      });
      const esAutorizadoSinVigencia = _firmado.estados_globales === ESTATUS_AUTORIZADO
        && !_firmado.vigencia_final;
      setDisableFields(_firmado.estados_globales !== ESTATUS_ACTIVO);
      setSoloVigenciaFinal(esAutorizadoSinVigencia);
      setVisible(true);
    }
    setLoading(false);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const [key] = selectedRowKeys;
      const values = getFormattedValues(form.getFieldsValue(), { clean: false });
      CAMPOS_SOLO_LECTURA.forEach((campo) => delete values[campo]);
      const saved = await (key
        ? patchFirmadoDeDocumento(key, soloVigenciaFinal
          ? { vigencia_final: values.vigencia_final }
          : values)
        : postFirmadoDeDocumento(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (key && await deleteFirmadoDeDocumento(key)) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const cambiarEstatus = async (estado, mensaje) => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (key && await patchFirmadoDeDocumento(key, { estados_globales: estado }, mensaje)) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    form.resetFields();
    setVisible(true);
  };

  const descripcionSeleccionado = `${selectedData?.documento_firmado_descripcion || ''} - ${
    selectedData?.plaza_clave_y_descripcion || ''}`;

  const columns = [
    {
      titleText: 'Módulo',
      dataIndex: 'modulo_descripcion',
      key: 'modulo_descripcion',
      width: 150,
    },
    {
      titleText: 'Documento',
      dataIndex: 'documento_firmado_descripcion',
      key: 'documento_firmado_descripcion',
      width: 200,
    },
    {
      titleText: 'Tipo Firma',
      dataIndex: 'tipo_de_firma_nombre',
      key: 'tipo_de_firma_nombre',
      width: 130,
    },
    {
      titleText: 'Tipo Impresión Firma Electrónica',
      dataIndex: 'tipo_de_impresion_nombre',
      key: 'tipo_de_impresion_nombre',
      width: 160,
    },
    {
      titleText: 'Puesto',
      dataIndex: 'puesto_clave_y_descripcion',
      key: 'puesto_clave_y_descripcion',
      width: 200,
    },
    {
      titleText: 'Área',
      dataIndex: 'directorio_de_area_actual_descripcion',
      key: 'directorio_de_area_actual_descripcion',
      width: 230,
    },
    {
      titleText: 'Empleado',
      dataIndex: 'empleado_actual_descripcion',
      key: 'empleado_actual_descripcion',
      width: 250,
    },
    {
      titleText: 'No. Plaza',
      dataIndex: 'plaza_clave_y_descripcion',
      key: 'plaza_clave_y_descripcion',
      width: 220,
    },
    {
      titleText: 'Rol de Firma',
      dataIndex: 'rol_de_firma_nombre',
      key: 'rol_de_firma_nombre',
      width: 130,
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 120,
      render: (val) => (val ? val?.format(DATE_FRONTEND_FORMAT) : ''),
    },
    {
      titleText: 'Orden Izquierda a Derecha',
      dataIndex: 'orden',
      key: 'orden',
      width: 120,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 110,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    orden: [
      requiredRule,
      {
        type: 'number',
        min: 1,
        message: 'El orden debe ser mayor a cero',
      },
    ],
  };

  const estadoSeleccionado = selectedData?.estados_globales;

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete: () => setVisibleAlert(true),
          }}
          customActions={[
            {
              onClick: () => setVisibleAutorizar(true),
              permission: { model: modelPermissionName, action: 'autorizar' },
              icon: <CheckCircleOutlined />,
              text: 'Autorizar',
              disabledBySelection: true,
              disabled: estadoSeleccionado !== ESTATUS_ACTIVO,
              className: 'success',
            },
            {
              onClick: () => setVisibleCancelar(true),
              permission: { model: modelPermissionName, action: 'cancelar' },
              icon: <CloseCircleOutlined />,
              text: 'Cancelar',
              disabledBySelection: true,
              disabled: ![ESTATUS_ACTIVO, ESTATUS_AUTORIZADO].includes(estadoSeleccionado),
              className: 'cancel',
            },
            {
              onClick: () => setVisibleReactivar(true),
              permission: { model: modelPermissionName, action: 'reactivar' },
              icon: <RedoOutlined />,
              text: 'Reactivar',
              disabledBySelection: true,
              // Igual que en el resto del sistema: cualquier estatus distinto de Activo.
              disabled: estadoSeleccionado === ESTATUS_ACTIVO,
              className: 'success',
            },
          ]}
          disableDelete={[ESTATUS_AUTORIZADO, ESTATUS_CANCELADO].includes(estadoSeleccionado)}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Configurador de Firmado de Documento`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableFields || soloVigenciaFinal}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            initialValues={{
              estados_globales: ESTATUS_ACTIVO,
            }}
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="modulo"
                label="Módulo"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  dataSource={modulos}
                  keyProp="id"
                  labelProp="descripcion"
                  disabled={disableFields}
                  onChange={(val) => {
                    form.setFieldsValue({ documento_firmado: undefined });
                    cargarDocumentos(val);
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="documento_firmado"
                label="Documento"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  dataSource={documentos}
                  keyProp="id"
                  labelProp="descripcion"
                  disabled={disableFields || !documentos.length}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="tipo_de_firma"
                label="Tipo de Firma"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  dataSource={tiposDeFirma}
                  keyProp="id"
                  labelProp="nombre"
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="tipo_de_impresion"
                label="Tipo de Impresión"
              >
                <Select
                  dataSource={tiposDeImpresion}
                  keyProp="id"
                  labelProp="nombre"
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="puesto"
                label="Puesto"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  dataSource={puestos}
                  keyProp="id"
                  labelProp="clave_y_descripcion"
                  disabled={disableFields}
                  onChange={(val) => {
                    form.setFieldsValue({ plaza: undefined });
                    cargarPlazas(val);
                    cargarDatosDePlaza(null);
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="plaza"
                label="No. Plaza"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  dataSource={plazas}
                  keyProp="id"
                  labelProp="clave_y_descripcion"
                  disabled={disableFields || !plazas.length}
                  onChange={(val) => cargarDatosDePlaza(val)}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="directorio_de_area_descripcion"
                label="Área"
              >
                <Input disabled />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="empleado_descripcion"
                label="No. Empleado"
              >
                <Input disabled />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="rol_de_firma"
                label="Rol de Firma"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  dataSource={rolesDeFirma}
                  keyProp="id"
                  labelProp="nombre"
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="vigencia_final"
                label="Vigencia Final"
              >
                <DatePicker
                  allowClear
                  format={DATE_FRONTEND_FORMAT}
                  style={{ width: '100%' }}
                  disabled={disableFields && !soloVigenciaFinal}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="orden"
                label="Orden Izquierda a Derecha"
                rules={rules.orden}
                hasFeedback
              >
                <InputNumber
                  min={1}
                  precision={0}
                  style={{ width: '100%' }}
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  // Siempre bloqueado: nace Activo, pasa a Autorizado/Cancelado con las
                  // acciones de la tabla y a Inactivo al llegar su Vigencia Final.
                  disabled
                />
              </Form.Item>
            </Col>

            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Configurador de Firmado ${descripcionSeleccionado}`}
          loading={loading}
        />

        <ModalConfirmation
          onFinish={() => cambiarEstatus(ESTATUS_AUTORIZADO, 'Autorizado correctamente')}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`autorizar el Configurador de Firmado ${descripcionSeleccionado}`}
          open={visibleAutorizar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => cambiarEstatus(ESTATUS_CANCELADO, 'Cancelado correctamente')}
          ConfirmCancel
          onCancel={() => setVisibleCancelar(false)}
          contentTitle={`cancelar el Configurador de Firmado ${descripcionSeleccionado}`}
          open={visibleCancelar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => cambiarEstatus(ESTATUS_ACTIVO, 'Reactivado correctamente')}
          onFinishText="Reactivar"
          onFinishIcon={<RedoOutlined />}
          onCancel={() => setVisibleReactivar(false)}
          contentTitle={`reactivar el Configurador de Firmado ${descripcionSeleccionado}`}
          open={visibleReactivar}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default ConfiguradorFirmadoDeDocumentos;
